import {
  RoleLevel,
  ROLE_LEVELS,
  ROLE_NAMES_TO_LEVELS,
  DEFAULT_PERMISSION_CONFIG,
  PermissionConfig,
} from "@/types/permissions";

/**
 * Service class for handling role-based access control
 */
export class PermissionService {
  private static instance: PermissionService;
  private config: PermissionConfig;

  private constructor(config: PermissionConfig = DEFAULT_PERMISSION_CONFIG) {
    this.config = config;
  }

  public static getInstance(config?: PermissionConfig): PermissionService {
    if (!PermissionService.instance) {
      PermissionService.instance = new PermissionService(config);
    }
    return PermissionService.instance;
  }

  /**
   * Get role level from role name
   */
  getRoleLevelFromName(roleName: string): RoleLevel {
    return (
      ROLE_NAMES_TO_LEVELS[roleName as keyof typeof ROLE_NAMES_TO_LEVELS] || ROLE_LEVELS.EXTERNAL
    );
  }

  /**
   * Get role level from role ID
   */
  getRoleLevelFromId(roleId: number): RoleLevel {
    switch (roleId) {
      case 1:
        return ROLE_LEVELS.EXTERNAL;
      case 2:
        return ROLE_LEVELS.CONTRIBUTOR;
      case 3:
        return ROLE_LEVELS.PROJECT_MANAGER;
      case 4:
        return ROLE_LEVELS.ADMIN;
      default:
        return ROLE_LEVELS.EXTERNAL;
    }
  }

  /**
   * Check if a role level has permission for a specific action
   */
  hasActionPermission(userLevel: RoleLevel, resource: string, action: string): boolean {
    const requiredLevel = this.config.actions[resource]?.[action];
    if (requiredLevel === undefined) {
      return false; // Action not defined, deny by default
    }
    return userLevel >= requiredLevel;
  }

  /**
   * Check if a role level has access to a specific page
   */
  hasPageAccess(userLevel: RoleLevel, page: string): boolean {
    const requiredLevel = this.config.pages[page];
    if (requiredLevel === undefined) {
      return false; // Page not defined, deny by default
    }
    return userLevel >= requiredLevel;
  }

  /**
   * Get all accessible pages for a role level
   */
  getAccessiblePages(userLevel: RoleLevel): string[] {
    return Object.entries(this.config.pages)
      .filter(([, requiredLevel]) => userLevel >= requiredLevel)
      .map(([page]) => page);
  }

  /**
   * Generate action permissions object for a role level
   */
  getActionPermissions(userLevel: RoleLevel): any {
    const result: any = {};

    Object.entries(this.config.actions).forEach(([resource, actions]) => {
      const resourceActions: any = {};
      Object.entries(actions).forEach(([action, requiredLevel]) => {
        resourceActions[action] = userLevel >= requiredLevel;
      });

      // Convert resource name to match frontend expectations
      const resourceKey = resource.replace("-", "_");
      result[resourceKey] = resourceActions;
    });

    return result;
  }

  /**
   * Update permission configuration
   */
  updateConfig(newConfig: Partial<PermissionConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

/**
 * Custom hook for permission checking with role level
 */
// eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix
export const usePermissionsByRole = (roleLevel: RoleLevel) => {
  const permissionService = PermissionService.getInstance();

  const hasPermission = (resource: string, action: string): boolean => {
    return permissionService.hasActionPermission(roleLevel, resource, action);
  };

  const hasPageAccess = (page: string): boolean => {
    return permissionService.hasPageAccess(roleLevel, page);
  };

  const getAccessiblePages = (): string[] => {
    return permissionService.getAccessiblePages(roleLevel);
  };

  const getActionPermissions = () => {
    return permissionService.getActionPermissions(roleLevel);
  };

  return {
    hasPermission,
    hasPageAccess,
    getAccessiblePages,
    getActionPermissions,
    roleLevel,
  };
};
